import { useEffect, useState } from 'react';
import DashboardCard from '../../components/UI/DashboardCard';
import { FiCalendar, FiUsers, FiDollarSign, FiStar } from 'react-icons/fi';

const API_BASE_URL = 'https://saloonbackend.dockyardsoftware.com/api';

const AdminDashboard = () => {
  const [todayAppointments, setTodayAppointments] = useState(0);
  const [staffCount, setStaffCount] = useState(0);
  const [recentAppointments, setRecentAppointments] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(true);
  const [loadingStaff, setLoadingStaff] = useState(true);
  const [loadingRecent, setLoadingRecent] = useState(true);
  const [revenue, setRevenue] = useState(null);
  const [loadingRevenue, setLoadingRevenue] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('Authentication token missing');
      setLoadingAppointments(false);
      setLoadingStaff(false);
      setLoadingRecent(false);
      setLoadingRevenue(false);
      return;
    }

    const fetchTodayAppointments = async () => {
      try {
        setLoadingAppointments(true);
        const response = await fetch(`${API_BASE_URL}/Appointment/count-today`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!response.ok) throw new Error(`Failed to fetch: ${response.statusText}`);
        const data = await response.json();
        setTodayAppointments(data.TodayCount ?? 0);
      } catch (error) {
        console.error("Error fetching today's appointments:", error);
        setTodayAppointments(0);
      } finally {
        setLoadingAppointments(false);
      }
    };

    const fetchStaffCount = async () => {
      try {
        setLoadingStaff(true);
        const response = await fetch(`${API_BASE_URL}/AdminAuth/staffcount`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!response.ok) throw new Error(`Failed to fetch staff count: ${response.statusText}`);
        const data = await response.json();
        setStaffCount(data.StaffCount ?? 0);
      } catch (error) {
        console.error('Error fetching staff count:', error);
        setStaffCount(0);
      } finally {
        setLoadingStaff(false);
      }
    };

    const fetchRecentAppointments = async () => {
      try {
        setLoadingRecent(true);
        const response = await fetch(`${API_BASE_URL}/Appointment/recent?count=5`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!response.ok) throw new Error(`Failed to fetch recent appointments: ${response.statusText}`);
        const data = await response.json();
        setRecentAppointments(data);
      } catch (error) {
        console.error('Error fetching recent appointments:', error);
        setRecentAppointments([]);
      } finally {
        setLoadingRecent(false);
      }
      
    };

    const fetchRevenue = async () => {
      try {
        setLoadingRevenue(true);
        const response = await fetch(`${API_BASE_URL}/AdminAuth/monthly-revenue`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            Year: new Date().getFullYear(),
            Month: new Date().getMonth() + 1
          })
        });

        if (!response.ok) throw new Error(`Failed to fetch revenue: ${response.statusText}`);
        const data = await response.json();
        setRevenue(data.Revenue ?? 0);
      } catch (error) {
        console.error('Error fetching revenue:', error);
        setRevenue(0);
      } finally {
        setLoadingRevenue(false);
      }
    };

    fetchTodayAppointments();
    fetchStaffCount();
    fetchRecentAppointments();
    fetchRevenue();
  }, []);


  const stats = {
    appointments: todayAppointments,
    staffMembers: staffCount,
    revenue: revenue,
    rating: 4.8
  };

  // Helper to format datetime from separate date and time strings
  const formatDateTime = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return 'Invalid Date';
    // Combine to ISO 8601 format for Date parsing
    const isoString = dateStr.split('T')[0] + 'T' + timeStr;
    const dateObj = new Date(isoString);
    return isNaN(dateObj) ? 'Invalid Date' : dateObj.toLocaleString();
  };

  // Helper to render appointment status with color badges
  const renderStatus = (status) => {
    let bgColor = 'bg-purple-900 text-purple-200'; // default
    if (!status) return <span className={`px-2 py-1 text-xs rounded-full ${bgColor}`}>Unknown</span>;

    switch (status.toLowerCase()) {
      case 'completed':
        bgColor = 'bg-green-900 text-green-200';
        break;
      case 'scheduled':
        bgColor = 'bg-yellow-900 text-yellow-200';
        break;
      case 'cancelled':
      case 'canceled':
        bgColor = 'bg-red-900 text-red-200';
        break;
      // add more statuses as needed
      default:
        bgColor = 'bg-purple-900 text-purple-200';
    }

    return (
      <span className={`px-2 py-1 text-xs rounded-full ${bgColor}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <h2 className="text-2xl font-bold mb-8">Admin Dashboard</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <DashboardCard
            icon={<FiCalendar size={24} />}
            title="Today's Appointments"
            value={loadingAppointments ? 'Loading...' : stats.appointments}
            change=""
            color="purple"
          />
          <DashboardCard
            icon={<FiUsers size={24} />}
            title="Staff Members"
            value={loadingStaff ? 'Loading...' : stats.staffMembers}
            change="2 on leave"
            color="blue"
          />
          <DashboardCard
            icon={<FiDollarSign size={24} />}
            title="Monthly Revenue"
            value={`Rs.${stats.revenue}`}
            change="12% from last month"
            color="green"
          />
          <DashboardCard
            icon={<FiStar size={24} />}
            title="Average Rating"
            value={stats.rating}
            change="+0.2 this month"
            color="yellow"
          />
        </div>

        <div className="bg-gray-800 rounded-xl p-6 mb-8">
          <h3 className="text-xl font-semibold mb-4">Recent Appointments</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Client</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Services</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Time</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {loadingRecent ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-3 text-center text-gray-400">Loading...</td>
                  </tr>
                ) : recentAppointments.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-3 text-center text-gray-400">No recent appointments found.</td>
                  </tr>
                ) : (
                  recentAppointments.map((appointment) => (
                    <tr key={appointment.Id || appointment.id} className="hover:bg-gray-700 transition">
                      <td className="px-4 py-3 whitespace-nowrap">{appointment.UserName || appointment.userName || 'N/A'}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {appointment.ServiceName || 'N/A'}

                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {formatDateTime(appointment.AppointmentDate || appointment.appointmentDate, appointment.AppointmentTime || appointment.appointmentTime)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {renderStatus(appointment.Status || appointment.status || 'Upcoming')}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
};

export default AdminDashboard;
